import type { TokenProvider } from './auth';
import { errorFromResponse } from './errors';

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  /** Send the bearer token (default true); `false` for `/login` and friends. */
  auth?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export interface ApiClientOptions {
  /** e.g. `/web/v1` */
  baseUrl: string;
  auth: TokenProvider;
  fetch?: typeof fetch;
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * Generic JSON client (trimmed from the nyceBuddy3 api-client): bearer from the `TokenProvider`, one refresh + retry on
 * 401, `ApiError` for every non-2xx response. Endpoint helpers live in the app.
 */
export function createApiClient(options: ApiClientOptions) {
  const baseUrl = options.baseUrl.replace(/\/+$/, '');
  const fetchImpl: typeof fetch = options.fetch ?? ((...args) => globalThis.fetch(...args));

  async function headers(withAuth: boolean, extra?: Record<string, string>): Promise<Record<string, string>> {
    const h: Record<string, string> = { 'Content-Type': 'application/json', ...extra };
    // An empty Content-Type means "let the request set it": FormData needs the browser to add the multipart boundary.
    if (h['Content-Type'] === '') delete h['Content-Type'];
    if (withAuth) {
      const token = await options.auth.getToken();
      if (token) h['Authorization'] = `Bearer ${token}`;
    }
    return h;
  }

  /** One refresh on 401, then the request is retried once with the new token. */
  async function withRefresh(send: (token?: string) => Promise<Response>): Promise<Response> {
    let response = await send();
    if (response.status !== 401) return response;
    const fresh = options.auth.refresh ? await options.auth.refresh().catch(() => null) : null;
    if (!fresh) {
      options.auth.onAuthExpired?.(options.auth.refresh ? 'refresh-failed' : 'unauthorized');
      return response;
    }
    response = await send(fresh);
    if (response.status === 401) options.auth.onAuthExpired?.('unauthorized');
    return response;
  }

  async function request<T>(
    method: Method,
    path: string,
    body?: unknown,
    opts: RequestOptions = {}
  ): Promise<T> {
    const withAuth = opts.auth !== false;
    const send = async (token?: string) => {
      const h = await headers(withAuth, opts.headers);
      if (token) h['Authorization'] = `Bearer ${token}`;
      return fetchImpl(`${baseUrl}${path}`, {
        method,
        headers: h,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: opts.signal,
      });
    };
    const response = withAuth ? await withRefresh(send) : await send();
    if (!response.ok) throw await errorFromResponse(response);
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /** Like `request` but returns the raw `Response` (streams, downloads). */
  async function requestRaw(
    method: Method,
    path: string,
    body: BodyInit | undefined,
    opts: RequestOptions & { accept?: string } = {}
  ): Promise<Response> {
    const withAuth = opts.auth !== false;
    const send = async (token?: string) => {
      const h = await headers(withAuth, { ...(opts.accept ? { Accept: opts.accept } : {}), ...opts.headers });
      if (token) h['Authorization'] = `Bearer ${token}`;
      return fetchImpl(`${baseUrl}${path}`, { method, headers: h, body, signal: opts.signal });
    };
    const response = withAuth ? await withRefresh(send) : await send();
    if (!response.ok) throw await errorFromResponse(response);
    return response;
  }

  return {
    baseUrl,
    request,
    requestRaw,
    get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body, opts),
    put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body, opts),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body, opts),
    del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
  };
}
